import type { DatumEntity } from "building-types/datums/datumEntityTypes";
import type { ViewFilter } from "building-types/views/viewFilterTypes";
import type { Table } from "common/data/table";

export type DatumTagFilter = (datums: Table<DatumEntity>) => Table<DatumEntity>;

export function datumMatchesViewFilter(datum: DatumEntity, viewFilter: Partial<ViewFilter>): boolean {
  const activeStep = viewFilter.datumTag;
  if (activeStep === undefined) {
    return true;
  }
  if (!("datumScope" in datum) || datum.datumScope === undefined) {
    return false;
  }
  return datum.datumScope.step === activeStep;
}

export function filterDatumsByViewFilter(
  datums: Table<DatumEntity>,
  viewFilter: Partial<ViewFilter>,
): Table<DatumEntity> {
  const filtered: Record<string, DatumEntity> = {};
  for (const datumId in datums) {
    const datum = datums[datumId];
    if (datumMatchesViewFilter(datum, viewFilter)) {
      filtered[datumId] = datum;
    }
  }
  return filtered;
}

export function applyDatumTagFilterForWallPlane(
  datums: Table<DatumEntity>,
  datumTagFilter: DatumTagFilter | undefined,
  planeHash: string,
  activeStepDatumTag: string | undefined,
): Table<DatumEntity> {
  const stepFiltered = applyDatumTagFilter(datums, datumTagFilter);
  // When no step tag is active, skip plane-hash scoping entirely.
  if (activeStepDatumTag === undefined) {
    return stepFiltered;
  }
  const filtered: Record<string, DatumEntity> = {};
  for (const datumId in stepFiltered) {
    const datum = stepFiltered[datumId];
    if (!("datumScope" in datum) || datum.datumScope === undefined) {
      continue;
    }
    const scope = datum.datumScope;
    // When datumTagFilter ran, step mismatches were already removed; only filter by step here
    // when callers pass activeStepDatumTag without a view-filter predicate.
    if (datumTagFilter === undefined && scope.step !== activeStepDatumTag) {
      continue;
    }
    if (!scope.planeHashes.includes(planeHash)) {
      continue;
    }
    filtered[datumId] = datum;
  }
  return filtered;
}

export function datumTagFilterFromViewFilter(viewFilter: Partial<ViewFilter>): DatumTagFilter {
  return (datums: Table<DatumEntity>) => filterDatumsByViewFilter(datums, viewFilter);
}

export function applyDatumTagFilter(
  datums: Table<DatumEntity>,
  datumTagFilter: DatumTagFilter | undefined,
): Table<DatumEntity> {
  if (!datumTagFilter) {
    return datums;
  }
  return datumTagFilter(datums);
}
